import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { fetchData, getOrCreatePlayerForUser } from "../utils/api.js";
import { formatLocalDate, getSeasonPhase } from "../utils/dateHelpers.js";
import {
  parseSlotKey,
  slotsFromRegistration,
  formatOfferingLabel,
} from "./common/registrationGridUtils.js";
import { CalendarIcon, ClockIcon } from "./common/Icons.js";
import appleLogo from "../assets/external/apple.svg";
import { ChipMedallion } from "./common/ChipMedallion.js";
import "./LeagueRegistration.css";

const BASE_DAYS = [
  { full: "Sunday", short: "Sun" },
  { full: "Monday", short: "Mon" },
  { full: "Tuesday", short: "Tue" },
  { full: "Wednesday", short: "Wed" },
  { full: "Thursday", short: "Thu" },
  { full: "Friday", short: "Fri" },
  { full: "Saturday", short: "Sat" },
];

const DEFAULT_GENDERS = ["Men's", "Women's", "Co-ed"];

const normalizeGenderKey = (gender) => {
  const lower = (gender || "").toLowerCase();
  if (lower.includes("women") || lower.includes("girl")) return "women";
  if (lower.includes("co-ed") || lower.includes("coed")) return "coed";
  if (lower.includes("men") || lower.includes("boy")) return "men";
  return lower;
};

const formatClockTime = (hhmm) => {
  if (!hhmm || typeof hhmm !== "string") return "";
  const [hStr, mStr = "00"] = hhmm.split(":");
  const hourNum = Number(hStr);
  if (Number.isNaN(hourNum)) return hhmm;
  const suffix = hourNum >= 12 ? "PM" : "AM";
  const hour12 = hourNum % 12 || 12;
  return `${hour12}:${mStr.slice(0, 2)} ${suffix}`;
};

const formatGameTimeRange = (season, league) => {
  const start =
    season?.mixins?.default_start_time ||
    league?.mixins?.default_start_time ||
    "18:00";
  const end =
    season?.mixins?.default_end_time ||
    league?.mixins?.default_end_time ||
    "22:00";
  return `${formatClockTime(start)} - ${formatClockTime(end)}`;
};

const formatDateWindow = (start, end) => {
  if (start && end) {
    return `${formatLocalDate(start)} - ${formatLocalDate(end)}`;
  }
  if (start) return formatLocalDate(start);
  return "TBD";
};

const extractSeasonOfferings = (season, league) => {
  if (!season) return [];
  const slotSet = slotsFromRegistration(season, league);
  if (slotSet.size > 0) {
    return [...slotSet].map((key) => parseSlotKey(key));
  }
  const legacyLabels = Array.isArray(season.divisions) ? season.divisions : [];
  const parsed = [];
  for (const label of legacyLabels) {
    const matchedDay = BASE_DAYS.find((d) =>
      label.toLowerCase().includes(d.full.toLowerCase()),
    );
    if (!matchedDay) continue;
    const genderLabel = DEFAULT_GENDERS.find(
      (g) => normalizeGenderKey(label) === normalizeGenderKey(g),
    );
    const cleanedDiv = label
      .replace(new RegExp(matchedDay.full, "i"), "")
      .replace(/men's|women's|co-ed|coed/gi, "")
      .trim();
    if (genderLabel && cleanedDiv) {
      parsed.push({
        day: matchedDay.full,
        gender: genderLabel,
        division: cleanedDiv,
      });
    }
  }
  return parsed;
};

const selectTargetSeason = (seasons, selectedSeason) => {
  if (selectedSeason) return selectedSeason;
  if (!Array.isArray(seasons) || seasons.length === 0) return null;
  const openReg = seasons.find((s) => getSeasonPhase(s) === "registration");
  return openReg || seasons[0];
};

const resolveDivisionId = async (seasonId, offeringLabel, divisionName) => {
  if (!seasonId) return null;
  try {
    const divs = await fetchData(`/divisions?season_id=eq.${seasonId}`);
    if (!Array.isArray(divs) || divs.length === 0) return null;
    const exact = divs.find(
      (d) =>
        (d.name || "").toLowerCase() === (offeringLabel || "").toLowerCase(),
    );
    if (exact) return exact.division_id || exact.id || null;
    const partial = divs.find((d) =>
      (d.name || "").toLowerCase().includes((divisionName || "").toLowerCase()),
    );
    return partial ? partial.division_id || partial.id || null : null;
  } catch {
    return null;
  }
};

const ConfirmRegistrationModal = ({
  offering,
  league,
  season,
  user,
  coachTeams,
  onClose,
  onRegistered,
}) => {
  const [selectedTeamId, setSelectedTeamId] = useState(
    () => coachTeams[0]?.team_id || coachTeams[0]?.id || "",
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  if (!offering) return null;

  const seasonName =
    season?.name ||
    [season?.season_type || season?.season, season?.year]
      .filter(Boolean)
      .join(" ");
  const offeringLabel = formatOfferingLabel(
    offering.division,
    offering.gender,
    offering.day,
  );
  const feeAmount =
    season?.price != null && Number(season.price) > 0
      ? `$${Number(season.price).toFixed(2)}`
      : "$0.00";

  const chosenTeam = coachTeams.find(
    (t) => String(t.team_id || t.id) === String(selectedTeamId),
  );
  let teamLineText = "Sign in as a team coach to register";
  if (chosenTeam) {
    teamLineText = chosenTeam.team_name || chosenTeam.name;
  } else if (user) {
    teamLineText = "Create a team to register";
  }

  const handleCheckout = async () => {
    setErrorMsg("");
    if (!user) {
      setErrorMsg("Please sign in to register a team.");
      return;
    }
    if (!selectedTeamId) {
      setErrorMsg("You need a team where you are coach to register.");
      return;
    }

    setIsSubmitting(true);
    try {
      const seasonId = season.season_id || season.id;
      const personId = await getOrCreatePlayerForUser(user);
      if (!personId) {
        throw new Error("Could not resolve player profile for registration.");
      }
      const divisionId = await resolveDivisionId(
        seasonId,
        offeringLabel,
        offering.division,
      );
      await fetchData("/registrations", {
        method: "POST",
        body: JSON.stringify({
          season_id: seasonId,
          person_id: personId,
          team_id: selectedTeamId,
          division_id: divisionId,
          status: "pending",
        }),
      });
      onRegistered?.(selectedTeamId);
      onClose();
    } catch (err) {
      setErrorMsg(err?.message || "Failed to complete registration.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div
        className="modal-confirm-overlay"
        onClick={onClose}
        aria-hidden="true"
      />
      <dialog
        open
        className="modal-confirm registration-confirm-dialog"
        aria-labelledby="confirm-registration-title"
      >
        <h3 id="confirm-registration-title">Confirm Registration</h3>

        {coachTeams.length > 1 && (
          <div
            className="registration-team-choices"
            role="radiogroup"
            aria-label="Select team"
          >
            {coachTeams.map((team) => {
              const tId = team.team_id || team.id;
              const isSelected = String(tId) === String(selectedTeamId);
              return (
                <button
                  key={tId}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  className={`filter-radio-option ${isSelected ? "is-selected" : ""}`.trim()}
                  onClick={() => setSelectedTeamId(tId)}
                >
                  <span className="filter-radio-circle" aria-hidden="true">
                    {isSelected && <span className="filter-radio-dot" />}
                  </span>
                  <span>{team.team_name || team.name}</span>
                </button>
              );
            })}
          </div>
        )}

        <ul className="registration-confirm-bullets">
          <li>- {teamLineText}</li>
          <li>
            - {league.name} - {seasonName}
          </li>
          <li>- {offeringLabel}</li>
          <li>- {feeAmount}</li>
        </ul>

        {errorMsg && (
          <div className="registration-error-banner" role="alert">
            {errorMsg}
          </div>
        )}

        <div className="registration-confirm-actions">
          <button
            type="button"
            className="apple-checkout-btn"
            disabled={isSubmitting}
            onClick={handleCheckout}
          >
            <img
              src={appleLogo}
              alt="Apple"
              style={{ width: 16, height: 16 }}
            />
            <span>{isSubmitting ? "Processing..." : "Pay"}</span>
          </button>

          <button
            type="button"
            className="cancel-registration-btn"
            onClick={onClose}
          >
            Cancel Registration
          </button>
        </div>
      </dialog>
    </>
  );
};

ConfirmRegistrationModal.propTypes = {
  offering: PropTypes.shape({
    day: PropTypes.string.isRequired,
    gender: PropTypes.string.isRequired,
    division: PropTypes.string.isRequired,
  }),
  league: PropTypes.object.isRequired,
  season: PropTypes.object,
  user: PropTypes.object,
  coachTeams: PropTypes.arrayOf(PropTypes.object),
  onClose: PropTypes.func.isRequired,
  onRegistered: PropTypes.func,
};

const LeagueRegistration = ({
  league,
  seasons = [],
  selectedSeason = null,
  user = null,
  isLeagueAdmin = false,
  coachTeams = [],
  onSeasonsChanged,
  onRegistered,
}) => {
  const [selectedOffering, setSelectedOffering] = useState(null);

  const activeSeason = useMemo(
    () => selectTargetSeason(seasons, selectedSeason),
    [seasons, selectedSeason],
  );

  const offerings = useMemo(
    () => extractSeasonOfferings(activeSeason, league),
    [activeSeason, league],
  );

  const visibleDays = useMemo(() => {
    const hasFriOrSat = offerings.some(
      (o) => o.day === "Friday" || o.day === "Saturday",
    );
    return hasFriOrSat ? BASE_DAYS : BASE_DAYS.slice(0, 5);
  }, [offerings]);

  const genderRows = useMemo(() => {
    const rawGenders =
      Array.isArray(league?.genders) && league.genders.length > 0
        ? league.genders.filter((g) => g !== "Open")
        : DEFAULT_GENDERS;
    return rawGenders.length > 0 ? rawGenders : DEFAULT_GENDERS;
  }, [league?.genders]);

  const uniqueDivisions = useMemo(
    () => [...new Set(offerings.map((o) => o.division))],
    [offerings],
  );

  if (!activeSeason) {
    return (
      <div className="league-registration-section">
        <div className="league-registration-header">
          <p style={{ margin: 0, color: "var(--color-text-secondary)" }}>
            No upcoming registration seasons available yet.
          </p>
        </div>
      </div>
    );
  }

  const seasonDisplayName =
    activeSeason.name ||
    [activeSeason.season_type || activeSeason.season, activeSeason.year]
      .filter(Boolean)
      .join(" ");

  return (
    <div className="league-registration-section">
      <div className="league-registration-header">
        <h3 className="league-registration-title">
          Upcoming Season: {league.name} - {seasonDisplayName}
        </h3>
      </div>

      <div className="league-registration-meta">
        <div className="league-registration-meta-group">
          <CalendarIcon size="sm" />
          <div className="league-registration-meta-lines">
            <span>
              Registration:{" "}
              {formatDateWindow(
                activeSeason.registration_start_date,
                activeSeason.registration_end_date,
              )}
            </span>
            <span>
              Games:{" "}
              {formatDateWindow(activeSeason.start_date, activeSeason.end_date)}
            </span>
          </div>
        </div>

        <div className="league-registration-meta-group">
          <ClockIcon size="sm" />
          <div className="league-registration-meta-lines">
            <span>Game Times: {formatGameTimeRange(activeSeason, league)}</span>
          </div>
        </div>
      </div>

      <div className="league-registration-matrix-wrap">
        <table className="league-registration-matrix">
          <thead>
            <tr>
              <th className="gender-col-header">Gender</th>
              {visibleDays.map((d) => (
                <th key={d.full}>{d.short}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {genderRows.map((gender) => {
              const gKey = normalizeGenderKey(gender);
              return (
                <tr key={gender}>
                  <td className="gender-row-label">{gender}</td>
                  {visibleDays.map((dayObj) => {
                    const cellOfferings = offerings.filter(
                      (o) =>
                        o.day === dayObj.full &&
                        normalizeGenderKey(o.gender) === gKey,
                    );
                    return (
                      <td key={dayObj.full}>
                        {cellOfferings.length > 0 && (
                          <div className="offering-cell-dots">
                            {cellOfferings.map((off) => (
                              <button
                                key={`${off.division}-${off.gender}-${off.day}`}
                                type="button"
                                className="offering-dot-btn"
                                title={`${off.division} ${gender} (${dayObj.short})`}
                                aria-label={`Register for ${off.division} ${gender} on ${dayObj.full}`}
                                onClick={() =>
                                  setSelectedOffering({
                                    ...off,
                                    gender,
                                  })
                                }
                              >
                                <ChipMedallion label={off.division} />
                              </button>
                            ))}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {uniqueDivisions.length > 0 && (
        <div className="league-registration-legend">
          {uniqueDivisions.map((div) => (
            <span key={div} className="league-registration-legend-item">
              <ChipMedallion label={div} />
              <span>{div}</span>
            </span>
          ))}
        </div>
      )}

      <ConfirmRegistrationModal
        offering={selectedOffering}
        league={league}
        season={activeSeason}
        user={user}
        coachTeams={coachTeams}
        onClose={() => setSelectedOffering(null)}
        onRegistered={onRegistered}
      />
    </div>
  );
};

LeagueRegistration.propTypes = {
  league: PropTypes.object.isRequired,
  seasons: PropTypes.arrayOf(PropTypes.object),
  selectedSeason: PropTypes.object,
  user: PropTypes.object,
  isLeagueAdmin: PropTypes.bool,
  coachTeams: PropTypes.arrayOf(PropTypes.object),
  onSeasonsChanged: PropTypes.func,
  onRegistered: PropTypes.func,
};

export default LeagueRegistration;
